import React from "react"
import { graphql } from "gatsby"

import findImage from "@/utils/find-image"

import MainLayout from "@/layouts/MainLayout"
import SharedHead from "@/components/SharedHead"

import PrimaryHeader from "@/components/PrimaryHeader"
import SplitPane from "@/components/SplitPane"
import SplitPaneSecondaryContent from "@/components/SplitPaneSecondaryContent"
import Accordion from "@/components/Accordion"
import AccordionItem from "@/components/AccordionItem"
import TryFreeCallToAction from "@/components/TryFreeCallToAction"
import IntegrationsShowcase from "@/components/IntegrationsShowcase"

import salesTeamVideo from "@/videos/teams-salesTeam.mp4"
import twofaVideo from "@/videos/2fa.mp4"
import integrationsVideo from "@/videos/sync-integrations.mp4"

import {
  Link,
  useTranslation,
  Trans,
} from "@zestia/gatsby-plugin-react-i18next"

import useHeadTranslation from "@/hooks/use-head-translation"

export const Head = props => {
  const { t } = useHeadTranslation(props)

  return (
    <SharedHead
      {...props}
      title={t("saas.meta-title")}
      description={t("saas.meta-description")}
    />
  )
}

const SaasPage = ({ data }) => {
  const { t } = useTranslation()

  return (
    <MainLayout
      trackingData={{
        content_category: "sector",
        intent: "bofu",
      }}
    >
      <PrimaryHeader
        backgroundClass="bg-orange-400"
        title={t("saas.header.title")}
        image={{
          src: findImage(data.allFile, "hero-saas"),
          alt: t("saas.header.image-alt"),
        }}
      >
        <p className="p--lg">{t("saas.header.description")}</p>
      </PrimaryHeader>

      <SplitPane
        assetAlignment="left"
        video={salesTeamVideo}
        title={t("saas.easy-implementation.title")}
        text={t("saas.easy-implementation.text")}
      />

      <SplitPane
        assetAlignment="right"
        video={twofaVideo}
        title={t("saas.safer-digital-world.title")}
        text={t("saas.safer-digital-world.text")}
      />

      <SplitPane
        assetAlignment="left"
        image={{
          src: findImage(data.allFile, "transpond-lists"),
          alt: t("saas.minimise-downtime.alt-text"),
        }}
        title={t("saas.minimise-downtime.title")}
        text={t("saas.minimise-downtime.text")}
      />

      <SplitPane
        assetAlignment="right"
        video={integrationsVideo}
        title={t("saas.integration-workflows.title")}
      >
        <SplitPaneSecondaryContent>
          <p className="mb-8 p--lg">
            <Trans
              i18nKey="saas.integration-workflows.text"
              components={{
                Accounting: (
                  <Link
                    className="text-link text-link--bare"
                    to="/integrations/#cats=invoicing-and-accounting"
                  />
                ),
                Marketing: (
                  <Link
                    className="text-link text-link--bare"
                    to="/integrations/#cats=marketing"
                  />
                ),
                HelpDesk: (
                  <Link
                    className="text-link text-link--bare"
                    to="/integrations/#cats=help-desk"
                  />
                ),
                Integration: (
                  <Link
                    className="text-link text-link--bare"
                    to="/integrations/#cats=integration-platforms"
                  />
                ),
              }}
            />
          </p>
        </SplitPaneSecondaryContent>
      </SplitPane>

      <IntegrationsShowcase
        title={t("common.integration-highlight-title-default")}
        description={t("saas.integration-highlight.description")}
      />

      <Accordion
        title={t("saas.accordion-saas.title")}
        description={t("saas.accordion-saas.description")}
      >
        <AccordionItem title={t("saas.accordion-saas.itemOne.title")}>
          <p>{t("saas.accordion-saas.itemOne.firstParagraph")}</p>
          <p>{t("saas.accordion-saas.itemOne.secondParagraph")}</p>
        </AccordionItem>
        <AccordionItem title={t("saas.accordion-saas.itemTwo.title")}>
          <p>{t("saas.accordion-saas.itemTwo.firstParagraph")}</p>
          <p>{t("saas.accordion-saas.itemTwo.secondParagraph")}</p>
        </AccordionItem>
        <AccordionItem title={t("saas.accordion-saas.itemThree.title")}>
          <p>{t("saas.accordion-saas.itemThree.firstParagraph")}</p>
          <p>{t("saas.accordion-saas.itemThree.secondParagraph")}</p>
        </AccordionItem>
        <AccordionItem title={t("saas.accordion-saas.itemFour.title")}>
          <p>{t("saas.accordion-saas.itemFour.firstParagraph")}</p>
          <p>{t("saas.accordion-saas.itemFour.secondParagraph")}</p>
          <p>{t("saas.accordion-saas.itemFour.thirdParagraph")}</p>
        </AccordionItem>
        <AccordionItem title={t("saas.accordion-saas.itemFive.title")}>
          <p>{t("saas.accordion-saas.itemFive.firstParagraph")}</p>
          <p>{t("saas.accordion-saas.itemFive.secondParagraph")}</p>
        </AccordionItem>
      </Accordion>

      <Accordion
        title={t("saas.accordion-mobile.title")}
        description={t("saas.accordion-mobile.description")}
      >
        <AccordionItem title={t("saas.accordion-mobile.itemOne.title")}>
          <p>{t("saas.accordion-mobile.itemOne.firstParagraph")}</p>
        </AccordionItem>
        <AccordionItem title={t("saas.accordion-mobile.itemTwo.title")}>
          <p>{t("saas.accordion-mobile.itemTwo.firstParagraph")}</p>
        </AccordionItem>
        <AccordionItem title={t("saas.accordion-mobile.itemThree.title")}>
          <p>{t("saas.accordion-mobile.itemThree.firstParagraph")}</p>
        </AccordionItem>
        <AccordionItem title={t("saas.accordion-mobile.itemFour.title")}>
          <p>
            <Trans
              i18nKey="saas.accordion-mobile.itemFour.firstParagraph"
              components={{
                CoordinateCalendars: (
                  <Link
                    className="text-link text-link--bare"
                    to="/features/tasks-and-calendar/"
                  />
                ),
              }}
            />
          </p>
        </AccordionItem>
        <AccordionItem title={t("saas.accordion-mobile.itemFive.title")}>
          <p>{t("saas.accordion-mobile.itemFive.firstParagraph")}</p>
        </AccordionItem>
      </Accordion>

      <SplitPane assetAlignment="stacked" title={t("saas.free-crm.title")}>
        <p className="mb-8 p--lg">{t("saas.free-crm.text")}</p>
        <p className="mb-8 p--lg">
          <Trans
            i18nKey="saas.free-crm.secondParagraph"
            components={{
              PaidPlans: (
                <Link className="text-link text-link--bare" to="/signup/" />
              ),
            }}
          />
        </p>
      </SplitPane>

      <TryFreeCallToAction description={t("saas.footer.description")} />
    </MainLayout>
  )
}

export const query = graphql`
  query ($language: String) {
    ...Locales
    ...Integrations
    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: { eq: "industries/saas" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`

export default SaasPage
